import { formatMoney } from '@theme/utilities';
import { GAMMES, gridUnitCents, minThreshold, nextThresholdFor, gridRows } from '@theme/group-order-pricing';

/**
 * <group-order-simulator> — simulateur de tarif groupe sur la landing
 * (design « Commande groupée 1b » : onglets gammes, saisie + slider, barre
 * segmentée des paliers, prix par paire + total, encart palier suivant).
 *
 * Purement indicatif : les prix réels sont recalculés au checkout par le
 * backend, sur le nombre total de paires de la commande.
 */
/** Résolution interne du slider : des POSITIONS (échelle par segments), pas des paires. */
const SLIDER_SCALE = 1000;
const INPUT_MAX = 5000;
/** La barre segmentée s'arrête au palier 1000+ ; au-delà → devis / saisie libre. */
const SEGMENT_MAX_FROM = 1001;

class GroupOrderSimulator extends HTMLElement {
  connectedCallback() {
    this.gamme = 'CLSQ';
    this.pairs = 25;

    this.refs = {};
    for (const element of this.querySelectorAll('[data-ref]')) {
      this.refs[element.dataset.ref] = element;
    }

    this.refs['gammes'].innerHTML = Object.entries(GAMMES)
      .map(([key, info]) => `<button type="button" class="group-simulator__tab" data-gamme="${key}">${info.label}</button>`)
      .join('');

    this.refs['gammes'].addEventListener('click', (event) => {
      const button = event.target.closest('[data-gamme]');
      if (!button) return;
      this.gamme = button.dataset.gamme;
      this.#render();
    });

    const onPairsInput = (value) => {
      this.pairs = Math.max(1, Math.min(INPUT_MAX, parseInt(value, 10) || 1));
      this.#render();
    };
    this.refs['slider'].addEventListener('input', (event) => onPairsInput(this.#posToPairs(Number(event.target.value))));
    this.refs['count'].addEventListener('input', (event) => onPairsInput(event.target.value));

    this.#render();
  }

  #segments() {
    return gridRows(this.gamme).filter((row) => row.from <= SEGMENT_MAX_FROM);
  }

  /**
   * Échelle du slider ALIGNÉE sur la barre segmentée : la piste est découpée en
   * autant de tranches égales que de cases de prix — le curseur est donc
   * toujours à l'aplomb de la tranche active (un slider linéaire 10-1000
   * plaçait 55 paires à 4 % de la piste, sous la mauvaise case).
   */
  #bounds() {
    const segments = this.#segments();
    const bounds = segments.map((row) => row.from);
    const last = segments[segments.length - 1];
    bounds.push(last.to !== null ? last.to + 1 : last.from * 2);
    return bounds;
  }

  #posToPairs(pos) {
    const bounds = this.#bounds();
    const count = bounds.length - 1;
    const width = SLIDER_SCALE / count;
    const index = Math.max(0, Math.min(count - 1, Math.floor(pos / width)));
    const frac = (pos - index * width) / width;
    return Math.round(bounds[index] + frac * (bounds[index + 1] - bounds[index]));
  }

  #pairsToPos(pairs) {
    const bounds = this.#bounds();
    const count = bounds.length - 1;
    const width = SLIDER_SCALE / count;
    if (pairs <= bounds[0]) return 0;
    for (let index = 0; index < count; index++) {
      if (pairs < bounds[index + 1]) {
        return index * width + (width * (pairs - bounds[index])) / (bounds[index + 1] - bounds[index]);
      }
    }
    return SLIDER_SCALE;
  }

  #render() {
    for (const button of this.refs['gammes'].querySelectorAll('[data-gamme]')) {
      button.classList.toggle('is-active', button.dataset.gamme === this.gamme);
    }
    this.refs['gamme-desc'].textContent = GAMMES[this.gamme].description;

    if (document.activeElement !== this.refs['count']) this.refs['count'].value = this.pairs;
    if (document.activeElement !== this.refs['slider']) {
      this.refs['slider'].value = this.#pairsToPos(this.pairs);
    }

    const unit = gridUnitCents(this.gamme, this.pairs);
    const min = minThreshold(this.gamme);
    const next = nextThresholdFor(this.gamme, this.pairs);

    const segments = this.#segments();
    this.refs['segments'].innerHTML = segments
      .map((row) => {
        const active = unit !== null && this.pairs >= row.from && (row.to === null || this.pairs <= row.to);
        const label = row.to === null ? `${row.from} paires et + : ${formatMoney(row.cents)}` : `${row.from} à ${row.to} paires : ${formatMoney(row.cents)}`;
        return `<div class="group-simulator__segment${active ? ' is-active' : ''}" title="${label}"><span>${formatMoney(row.cents)}</span></div>`;
      })
      .join('');
    this.refs['segment-labels'].innerHTML = segments
      .map((row, index) => `<span>${row.from}${index === segments.length - 1 ? '+' : ''}</span>`)
      .join('');

    if (unit === null) {
      const missing = min - this.pairs;
      this.refs['unit'].innerHTML = '—';
      this.refs['total'].textContent = 'Prix boutique';
      this.refs['hint'].textContent = `Le tarif groupe ${GAMMES[this.gamme].label} démarre à ${min} paires — encore ${missing} paire${missing > 1 ? 's' : ''}.`;
    } else {
      this.refs['unit'].innerHTML = `${formatMoney(unit)} <span>/ paire</span>`;
      this.refs['total'].textContent = `Total : ${formatMoney(unit * this.pairs)}`;
      if (next !== null) {
        const nextUnit = gridUnitCents(this.gamme, next);
        const missing = next - this.pairs;
        this.refs['hint'].textContent = `Plus que ${missing} paire${missing > 1 ? 's' : ''} pour passer à ${formatMoney(nextUnit)} / paire (dès ${next}).`;
      } else {
        this.refs['hint'].textContent = `Meilleur tarif atteint — ${formatMoney(unit)} la paire.`;
      }
    }
  }
}

if (!customElements.get('group-order-simulator')) {
  customElements.define('group-order-simulator', GroupOrderSimulator);
}
